import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Pagination } from '@/components/ui/pagination';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { paymentMethodLabelKey } from '@/features/events/payment-methods';
import { formatCurrency, formatDateTime, formatNumber, formatPhone } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useOverviewRows, type OverviewSearchModel } from './overview.api';
import {
  ACTIVITY_METRICS,
  COUNT_METRICS,
  SOURCE_VAR,
  sourceLabelKey,
} from './types';

/**
 * The rows behind one number on the money or activity tab.
 *
 * Every figure on that page is an aggregate, and an aggregate nobody can open
 * is a figure nobody can check. "Paid offline: $4,120" is either a fact or a
 * bug, and without this there is no way to tell which from the screen.
 *
 * The total comes from the API rather than being re-added from the rows on
 * screen: the rows are one page, the total is the whole set. It is also the
 * assertion the reader makes without being asked — if it does not match the
 * tile they clicked, something is wrong and they will see it immediately.
 */

const PAGE_SIZE = 25;

export function OverviewRowsSheet({
  metric,
  title,
  source,
  model,
  onClose,
}: {
  metric: number | null;
  title: string;
  source: number | null;
  model: OverviewSearchModel;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);

  // Metric and source together are the identity of what is open, so a new
  // question starts at page one rather than wherever the last list was left.
  useEffect(() => {
    setPage(0);
  }, [metric, source]);

  const query = useOverviewRows({
    model,
    metric,
    source,
    page,
    size: PAGE_SIZE,
  });

  const data = query.data;
  const rows = data?.rows ?? [];
  const isCount = metric !== null && COUNT_METRICS.includes(metric);
  // Activity rows are memberships, bookings and people, with no money on them.
  const hasAmounts = metric === null || !ACTIVITY_METRICS.includes(metric);
  // `isLoading`, not `isPending`: the query is disabled while the sheet is
  // closed. A page change keeps the previous page on screen, under a spinner.
  const loading = query.isLoading;
  const busy = query.isFetching && !loading;

  return (
    <Sheet
      open={metric !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-2xl"
      >
        <SheetHeader className="border-b pr-12">
          <SheetTitle>{title}</SheetTitle>
        </SheetHeader>

        <div className="space-y-3 p-4">
          {/* The headline first. It is what the reader came to check against
              the tile they clicked, so it has to be the same kind of figure
              that tile showed. */}
          <div className="bg-card border-l-primary relative rounded-xl border border-l-[3px] px-3 py-2">
            <BusyOverlay show={busy} size="sm" />
            <div className="flex items-baseline justify-between gap-2">
              <span className="text-muted-foreground text-xs">
                {t('overview.total')}
              </span>
              {loading ? (
                <Skeleton className="h-7 w-24" />
              ) : (
                <span className="text-xl font-semibold tabular-nums">
                  {isCount
                    ? formatNumber(data?.total ?? 0)
                    : formatCurrency(data?.sum)}
                </span>
              )}
            </div>
            {!hasAmounts ? null : loading ? (
              <Skeleton className="mt-1 h-3 w-20" />
            ) : (
              <p className="text-muted-foreground text-xs">
                {isCount
                  ? `${formatCurrency(data?.sum)} ${t('overview.value').toLowerCase()}`
                  : t('overview.nRows', { count: data?.total ?? 0 })}
              </p>
            )}
          </div>

          {loading && (
            <div className="divide-y rounded-xl border">
              {Array.from({ length: 6 }).map((_, index) => (
                <div
                  key={index}
                  className="flex items-start justify-between gap-3 p-3"
                >
                  <div className="flex-1 space-y-1.5">
                    <Skeleton className="h-4 w-40" />
                    <Skeleton className="h-3 w-52" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                  <Skeleton className="h-5 w-16" />
                </div>
              ))}
            </div>
          )}

          {query.isError && (
            <p className="text-muted-foreground py-16 text-center text-sm">
              {t('overview.loadFailed')}
            </p>
          )}

          {query.isSuccess && rows.length === 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={busy} />
              <p className="text-muted-foreground rounded-xl border border-dashed py-12 text-center text-sm">
                {t('overview.noData')}
              </p>
            </div>
          )}

          {rows.length > 0 && (
            <>
              <div className="relative rounded-xl">
                <BusyOverlay show={busy} />
                <div
                  aria-busy={busy}
                  className={cn(
                    'divide-y rounded-xl border',
                    busy && 'opacity-60 transition-opacity'
                  )}
                >
                  {rows.map((row) => (
                    <div
                      key={row.id}
                      className="flex items-start justify-between gap-3 p-3"
                    >
                      <div className="min-w-0 space-y-1">
                        <p className="truncate text-sm font-medium">{row.name}</p>
                        <p className="text-muted-foreground truncate text-xs">
                          {row.email}
                        </p>
                        {row.phoneNumber && (
                          <p className="text-muted-foreground truncate text-xs tabular-nums">
                            {formatPhone(row.phoneNumber)}
                          </p>
                        )}
                        <p className="text-muted-foreground text-xs">
                          {row.what}
                        </p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <Badge
                            variant="outline"
                            style={{
                              borderColor: SOURCE_VAR[row.kind],
                              color: SOURCE_VAR[row.kind],
                            }}
                          >
                            {t(sourceLabelKey(row.kind))}
                          </Badge>
                          {row.method !== null && (
                            <span className="text-muted-foreground text-xs">
                              {t(paymentMethodLabelKey(row.method))}
                            </span>
                          )}
                          {row.isOffline && (
                            <Badge variant="warning">
                              {t('overview.offline')}
                            </Badge>
                          )}
                          {row.isRenewal && (
                            <Badge variant="secondary">
                              {t('overview.renewal')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground text-xs">
                          {formatDateTime(row.when)}
                        </p>
                      </div>
                      {hasAmounts && (
                        <p className="shrink-0 font-semibold tabular-nums">
                          {formatCurrency(row.amount)}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Server-paginated: the API returns one page at a time and
                  reports the total, so nothing here slices a full set. */}
              <Pagination
                page={page}
                size={PAGE_SIZE}
                total={data?.total ?? 0}
                onPageChange={setPage}
              />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
